import { useCallback } from 'react';

import { useReferenceAssistantGateway } from './ReferenceAssistantGatewayProvider';
import { replaceCitationRequest } from '../api/citationRequestCache';

import { queryClient } from '@/util/queryClient';
import { Sentry } from '@/util/sentry';

const NOTIFY_MAX_ATTEMPTS = 3;

const NOTIFY_RETRY_DELAY_MS = 500;

/**
 * Persists the searching-phase "notify me when ready" opt-in, best-effort.
 * The endpoint is idempotent and the button that triggers it only renders
 * while the request is still in flight, so a transient transport failure is
 * exactly the retriable case: a few bounded attempts keep the "we'll let you
 * know" promise from being broken by a blip. On success the response
 * reconciles the cached `notifyWhenReady`; only once the attempts are
 * exhausted is the failure captured to Sentry.
 */
export function useNotifyWhenReady() {
  const gateway = useReferenceAssistantGateway();

  return useCallback(
    async (citationRequestId: string, conversationId: string, messageSid: string) => {
      for (let attempt = 1; attempt <= NOTIFY_MAX_ATTEMPTS; attempt += 1) {
        try {
          const updated = await gateway.notifyCitationRequest({
            citationRequestId,
            notifyWhenReady: true,
          });

          replaceCitationRequest(queryClient, conversationId, updated);

          return;
        } catch (notifyError) {
          if (attempt < NOTIFY_MAX_ATTEMPTS) {
            await new Promise((resolve) => setTimeout(resolve, NOTIFY_RETRY_DELAY_MS));

            continue;
          }

          Sentry.captureException(notifyError, {
            tags: { component: 'StatementTriagePhases', source: 'citation-request-notify' },
            extra: {
              errorMessage:
                notifyError instanceof Error ? notifyError.message : String(notifyError),
              citationRequestId,
              conversationId,
              messageSid,
              attempts: attempt,
            },
          });
        }
      }
    },
    [gateway]
  );
}
